import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag } from "lucide-react";

const REASONS = [
  { value: "spam", label: "Spam or misleading" },
  { value: "offensive", label: "Offensive or inappropriate" },
  { value: "wrong_business", label: "Wrong business attribution" },
  { value: "copyright", label: "Copyright / not my content" },
  { value: "other", label: "Other" },
];

export default function ReportPinDialog({ pinId, open, onOpenChange }) {
  const { user } = useAuth();
  const [reason, setReason] = useState("spam");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) {
      toast.info("Sign in to report a Find");
      onOpenChange(false);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/blinterest/pins/${pinId}/report`,
        { reason, note },
        { withCredentials: true },
      );
      toast.success("Thanks — we'll review this Find");
      setNote("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="report-pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-500" />
            Report this Find
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {REASONS.map((r) => (
            <label
              key={r.value}
              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${
                reason === r.value
                  ? "border-tbc-navy bg-tbc-cream"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`report-reason-${r.value}`}
            >
              <input
                type="radio"
                name="report-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={(e) => setReason(e.target.value)}
              />
              <span className="text-sm font-body text-tbc-navy">{r.label}</span>
            </label>
          ))}
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional: tell us more"
          rows={3}
          data-testid="report-note-input"
        />
        <Button
          onClick={submit}
          disabled={submitting}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full"
          data-testid="report-submit-btn"
        >
          {submitting ? "Submitting…" : "Submit Report"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
